import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { I18n } from 'react-redux-i18n'

import { botConsole } from 'api'
import { BrandsAndDelegations, SelectWithLabel, Button } from 'components/Form'
import ModalWrapper from 'components/ChannelView/ModalWrapper'
import { ALLOWED_DOMAINS } from 'constants/index'

const AddEditAssistant = ({ isOpenAddEditModal, toggleAddEditModal, assistantToEdit, setBotHeader, botHeader, assistants, setAssistants }) => {
  const { brands, delegations } = useSelector(store => store.centers)
  const [assistant, setAssistant] = useState({})
  const [errorMessages, setErrorMessages] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [copyFromOptions, setCopyFromOptions] = useState([])

  useEffect(() => {
    setAssistant({ nameIsEnable: true })

    const copyFromOptions = []
    assistants.forEach(assistant => {
      copyFromOptions.push({ value: assistant.consoleId, label: assistant.name.length ? assistant.name : 'Global' })
    })
    setCopyFromOptions(copyFromOptions)

    if (assistantToEdit) {
      let assistant = assistants.filter(assistant => assistant.consoleId === assistantToEdit)[0]
      assistant.nameIsEnable = assistant.name.length ? true : false
      assistant.brands = botHeader.brands
      assistant.delegations = botHeader.delegations
      setAssistant(assistant)
    }
  }, [])

  const handleAddEdit = async (assistant) => {
    setErrorMessages(null)
    setIsSubmitting(true)

    // Comprueba que el nombre del asistente a crear no esté vacío
    if ((!assistant.name || !assistant.name.length) && assistant.nameIsEnable) {
      setErrorMessages(I18n.t('botConsole_error_required_name'))
      setIsSubmitting(false)
      return
    }

    // Comprueba que el nombre del asistente a crear sea único
    const checkAssistant = assistants.find(savedAssistant => savedAssistant.name === assistant.name)
    if ((checkAssistant && assistantToEdit !== checkAssistant.consoleId) || assistant.name.toLowerCase() === 'global') {
      setErrorMessages(I18n.t('botConsole_error_required_nameExist'))
      setIsSubmitting(false)
      return
    }

    // Comprueba que las marcas del asistente a crear estén asignadas
    if (!assistant.brands || !assistant.brands.length) {
      setErrorMessages(I18n.t('botConsole_error_required_brand'))
      setIsSubmitting(false)
      return
    }

    // Comprueba que las delegaciones del asistente a crear estén asignadas
    if (!assistant.delegations || !assistant.delegations.length) {
      setErrorMessages(I18n.t('botConsole_error_required_delegation'))
      setIsSubmitting(false)
      return
    }

    // Comprueba que las delegaciones del asistente a crear no existan en otro asistente ya creado
    if (assistant.name) {
      for (const savedAssistant of assistants) {
        if (savedAssistant.name && assistant.consoleId !== assistantToEdit) {
          for (const savedDelegation of savedAssistant.delegations) {
            for (const delegation of assistant.delegations) {
              if (delegation.value === savedDelegation || delegation.value === savedDelegation._id) {
                setErrorMessages(I18n.t('botConsole_error_required_delegationExist'))
                setIsSubmitting(false)
                return
              }
            }
          }
        }
      }
    }

    const brandsToSave = []
    if (assistant.brands.some(brand => brand.value === 'all')) {
      brands.forEach(brand => brandsToSave.push({
        _id: brand._id,
        name: brand.name
      }))
    } else {
      assistant.brands.forEach(it => brandsToSave.push({
        _id: it.value || it._id,
        name: it.label || it.name
      }))
    }
    const delegationsToSave = []
    if (assistant.delegations.some(delegation => delegation.value === 'all')) {
      delegations.forEach(delegation => {
        if (!brandsToSave.find(brandToSave => brandToSave._id === delegation.brand)) return

        delegationsToSave.push({
          _id: delegation._id,
          name: delegation.name,
          brand: delegation.brand
        })
      })
    } else {
      assistant.delegations.forEach(it => delegationsToSave.push({
        _id: it.value || it._id,
        name: it.label || it.name,
        brand: it.data?.brand || it.brand
      }))
    }

    try {
      let result = null
      if (assistantToEdit) {
        result = await botConsole.updateBotHeader(assistantToEdit, { name: assistant.name, brands: brandsToSave, delegations: delegationsToSave })
      } else {
        result = await botConsole.createVa({ name: assistant.name, brands: brandsToSave, delegations: delegationsToSave, copyFrom: assistant.copyFrom ? assistant.copyFrom.value : '' })
      }

      if (result.status === 200) {
        const { data } = await botConsole.getVaList()
        setAssistants(data.result)
        setBotHeader({ ...botHeader, name: assistant.name, brands: brandsToSave, delegations: delegationsToSave })
        setTimeout(toggleAddEditModal)
      }
    } catch (error) {
      if (error.data.message.includes('Console for entity exist') || error.data.type === 'error_delegation_in_another_av') {
        setErrorMessages(I18n.t('botConsole_error_back_entityExist'))
      } else {
        setErrorMessages(I18n.t('botConsole_error_back_genericSave'))
      }
    }

    setIsSubmitting(false)
  }

  return (
    <ModalWrapper
      id='addEditModal'
      isOpen={isOpenAddEditModal}
      toggle={toggleAddEditModal}
      header={<h1>{assistantToEdit ? I18n.t('botConsole_modal_title_edit') : I18n.t('botConsole_modal_title_create')}</h1>}
      contentClassName='av-modal'
      body={
        <>
          {assistant.nameIsEnable && (
            <div className='name-input'>
              <label htmlFor='name'>{I18n.t('dashboard_assistant-name')}</label>
              <input
                type="text"
                id="name"
                placeholder={I18n.t('dashboard_assistant-name')}
                value={assistant.name || ''}
                onChange={event => setAssistant({ ...assistant, name: event.target.value })}
              />
            </div>
          )}
          <BrandsAndDelegations
            onChangeBrands={value => setAssistant(state => ({ ...state, brands: value }))}
            currentBrands={assistant.brands || []}
            onChangeDelegations={value => setAssistant(state => ({ ...state, delegations: value }))}
            currentDelegations={assistant.delegations || []}
            defaultPlaceholder
            grouped={false}
          />
          {!assistantToEdit && ALLOWED_DOMAINS.type !== 'TA' && (
            <div className='copy-options'>
              <label>{I18n.t('botConsole_select_copyFrom_label')}</label>
              <SelectWithLabel
                placeholder={I18n.t('dashboard_i-want-start-from-scratch')}
                options={copyFromOptions}
                value={assistant.copyFrom}
                onChange={value => setAssistant({
                  ...assistant,
                  copyFrom: value || { value: '', label: I18n.t('dashboard_i-want-start-from-scratch') }
                })}
                classNameWrapper='copy-options'
                isClearable
              />
            </div>
          )}
          {errorMessages && <p className='error-message'>{errorMessages}</p>}
        </>
      }
      footer={
        <>
          <Button secondary value={assistantToEdit ? I18n.t('dashboard_save') : I18n.t('dashboard_create')} onClick={() => handleAddEdit(assistant)} isSubmitting={isSubmitting} />
          <Button value={I18n.t('dashboard_cancel')} onClick={toggleAddEditModal} />
        </>
      }
    />
  )
}

export default AddEditAssistant
